// Third-party packages
import {
	CollectionJson,
} from '@@types/Collection';
import {
	UserJson,
} from '@@types/User';
import {
	EnumSpecificationSteps,
} from '@enums/steps.enum';
import {
	GetSpecificationError,
} from '@exceptions/GetSpecificationError';
import {
	GetSpecificationOffersError,
} from '@exceptions/GetSpecificationOffersError';
import {
	GetSpecificationStatusError,
} from '@exceptions/GetSpecificationStatusError';
import {
	handleErrorResponse,
} from '@modules/utils';
import {
	ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react';
import {
	useCookies,
} from 'react-cookie';
import {
	Trans,
	withTranslation,
} from 'react-i18next';
import {
	useDispatch,
	useSelector,
} from 'react-redux';
import {
	Outlet,
	useLocation,
	useParams,
} from 'react-router-dom';

// Project modules
import {
	getPublicOffers,
	getSpecification,
	getSpecificationStatus,
} from '@api/specification';
import {
	DeviceDisplays,
	DeviceOrientations,
} from '@stores/_slices/device';
import {
	setSpecificationOffers,
	StoreSpecificationOffers,
} from '@stores/_slices/specification_offers';
import {
	setSpecification,
} from '@stores/_slices/specifications';
import {
	ReducerInstance,
} from '@stores/lpdipro/reducers';
import {
	selectCurrentLanguage,
} from '@stores/selectors/appSelectors';

// Templates / Layouts / Pages
import {
	Page,
} from '@layouts/Pages/Page';
import {
	ContentLoader,
} from '@layouts/Pages/PageSpecification/contentLoader';
import {
	Header,
} from '@layouts/Pages/PageSpecification/header';
import {
	SpecificationOfferJson,
} from '@layouts/Pages/PageSpecification/manage/offer-row';
import {
	SpecificationJson,
} from '@layouts/Pages/PageSpecifications';

// UI Components
import {
	Button,
} from '@components/button';
import {
	StatusBanner,
	StatusBannerProps,
} from '@components/status-banner';

// Style imports
import styles from './PageSpecification.module.scss';

export interface PageSpecificationOutletContextType {
	specification: SpecificationJson | null;
	step: EnumSpecificationSteps;
	isLoaded: {
		offers: boolean;
		specification: boolean;
	};
	isDesktopResolution: boolean;
	language: string;
	offers: CollectionJson<SpecificationOfferJson>;
	statusMsgsElement: ReactNode;
	specificationOffersStatus: 'loading' | 'success' | 'error' | 'idle';
}

export interface OfferJson {
	collaborator?: string;
	id?: number;
	name?: string;
	search_criterias?: {
		area_min?: number;
		area_max?: number;
		locations?: Array<string>;
		natures?: Array<string>;
		transaction_types?: Array<string>;
	};
	stats?: {
		new: number;
		updated: number;
	};
	users?: UserJson[];
}

export const PageSpecification = withTranslation()(() => {
	const location = useLocation();
	const dispatch = useDispatch();
	const device = useSelector((state: ReducerInstance) => state.device.instance);
	const isDesktopResolution = device.display === DeviceDisplays.DESKTOP || (device.display === DeviceDisplays.TABLET && device.orientation === DeviceOrientations.LANDSCAPE);

	const {
		specificationid,
		step,
	} = useParams();
	const currentSpecificationId = !isNaN(Number(specificationid)) ? Number(specificationid) : undefined;
	const specificationStepName = step as EnumSpecificationSteps || EnumSpecificationSteps.MATCHED;

	const specifications = useSelector((state: ReducerInstance) => state.specifications.instance);
	const specification = specifications.collection.find((specification: SpecificationJson) => specification.id === currentSpecificationId);

	const offers = useSelector((state: ReducerInstance) => state.specification_offers.instance.collection.find((specification: StoreSpecificationOffers) => specification.id === currentSpecificationId))?.offers;

	const actualLanguage = useSelector(selectCurrentLanguage);

	const statusMsgs = useSelector((state: ReducerInstance) => state.status_msgs.instances);

	const [
		,
		setCookie,
	] = useCookies([
		'zendesk_widget_open',
	]);

	const statusMsgActions: Record<string, () => void> = {
		openZendesk: () => {
			setCookie('zendesk_widget_open', true, {
				maxAge: 60 * 60 * 24 * 7,
				path: '/',
			});
		},
	};

	const statusMsgsElement = statusMsgs.length ? (
		<div
			className={styles.status_banner_container}
			data-status-msg-wrapper
		>
			{statusMsgs.map((statusBanner: StatusBannerProps, index: number) => (
				<StatusBanner
					className={styles.status_banner}
					isClosable={statusBanner.isClosable}
					key={index}
					message={statusBanner.message}
					theme={statusBanner.theme}
				>
					<Trans
						i18nKey={statusBanner.message.i18nKey}
						values={statusBanner.message.values}
						transKeepBasicHtmlNodesFor={[
							'br',
							'strong',
						]}
						context={statusBanner.message.context}
					/>
					{statusBanner.buttons?.map((button, index) => (
						<Button
							{...button}
							key={index}
							label={button.label}
							theme={statusBanner.theme}
							onClick={statusMsgActions[button.id]}
						/>
					))}
				</StatusBanner>
			))}
		</div>
	) : null;

	const [
		isLoaded,
		setIsLoaded,
	] = useState({
		offers: false,
		specification: false,
	});

	const [
		specificationOffersStatus,
		setSpecificationOffersStatus,
	] = useState(undefined);

	useEffect(() => {
		const content = document.querySelector(`.${styles.PageSpecification__content}`) as HTMLElement | null;
		const errorElement = document.querySelector('[data-error="true"]') as HTMLElement | null;

		if (content && errorElement) {
			const errorElementRect = errorElement.getBoundingClientRect();
			const contentRect = content.getBoundingClientRect();
			content.scrollTop = errorElementRect.top - contentRect.top - content.scrollTop;
		} else if (content) {
			if (isDesktopResolution) {
				content.scrollTop = 0;
			} else {
				window.scrollTo(0, 0);
			}
		}
	}, [
		location,
	]);



	useEffect(() => {
		const controller = new AbortController();
		const fetchSpecificationStatus = async () => {
			const getSpecificationStatusResponse = await getSpecificationStatus(currentSpecificationId);

			handleErrorResponse(getSpecificationStatusResponse, GetSpecificationStatusError);
			if (getSpecificationStatusResponse.status === 200) {
				setSpecificationOffersStatus(getSpecificationStatusResponse.payload);
			}
		};

		fetchSpecificationStatus();

		const fetchSpecification = async () => {
			const getSpecificationResponse = await getSpecification(currentSpecificationId, controller);

			handleErrorResponse(getSpecificationResponse, GetSpecificationError);

			if (getSpecificationResponse.status === 200) {
				dispatch(setSpecification(getSpecificationResponse.payload));
			}

			const getOffersResponse = await getPublicOffers(currentSpecificationId);

			handleErrorResponse(getOffersResponse, GetSpecificationOffersError);

			if (getOffersResponse.status === 200) {
				dispatch(setSpecificationOffers({
					id: currentSpecificationId,
					offers: getOffersResponse.payload,
				}));
			}
		};

		fetchSpecification().finally(() => {
			setIsLoaded({
				...isLoaded,
				offers: true,
				specification: true,
			});
		});

		return () => controller.abort();
	}, [
		currentSpecificationId,
	]);

	const contentSpecificationRef = useRef<HTMLDivElement>(null);

	return (
		<Page className={styles.PageSpecification} >
			<div
				className={styles.PageSpecification__content}
				ref={contentSpecificationRef}
			>
				{isLoaded.specification && specification ? (
					<Header
						contentSpecificationRef={contentSpecificationRef}
						isDesktopResolution={isDesktopResolution}
						specification={specification}
						step={specificationStepName}
					/>
				) : <ContentLoader />}
				<Outlet
					context={{
						isDesktopResolution: isDesktopResolution,
						isLoaded: isLoaded,
						language: actualLanguage,
						offers: isLoaded.offers && offers ? offers : null,
						specification: isLoaded.specification && specification ? specification : null,
						specificationOffersStatus,
						statusMsgsElement,
						step: specificationStepName,
					} as PageSpecificationOutletContextType}
				/>
			</div>
		</Page>
	);

});
